/**
 * FeedbackHub — tickets del cliente (bugs, sugerencias y roadmap) al estándar.
 *
 * QUÉ HABÍA ANTES
 * ---------------------------------------------------------------------------
 * Todo a la vez en la misma pantalla: un formulario de alta permanente a la
 * izquierda, la lista de tickets en medio y el detalle a la derecha, cada uno en
 * su tarjeta de Bootstrap con **su propio CSS** (`FeedbackHub.css`). El detalle
 * llevaba dentro el cambio de estado, los adjuntos, la descripción enriquecida y
 * el hilo de comentarios, así que para leer un ticket había que interpretar tres
 * columnas a la vez. Además el resumen eran píldoras «Nuevo: 3» sueltas y no
 * había buscador de verdad, ni filtros de prioridad, ni columnas, ni export.
 *
 * AHORA
 * ---------------------------------------------------------------------------
 * Listado estándar de tickets (buscador, filtros, chips de KPI, columnas
 * elegibles, vistas Lista/Fichas y export) y **el ticket se abre en el diálogo
 * común**, que es donde tienen sentido sus cosas: estado, prioridad, categoría y
 * visibilidad (se guardan al cambiarlas), votos, la descripción con formato y
 * sus adjuntos, el historial de comentarios y la edición del texto.
 *
 * El alta también pasa a diálogo: ya no hay un formulario fijo ocupando un tercio
 * de la pantalla para algo que se usa de vez en cuando.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ArrowPathIcon,
  PlusIcon,
  HandThumbUpIcon,
  PaperClipIcon,
  PencilSquareIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  DocumentTextIcon,
  LockClosedIcon,
  GlobeAltIcon,
  CheckIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import RichTextEditor from './RichTextEditor';
import FeedbackAttachmentsField from './FeedbackAttachmentsField';
import { hace } from '../utils/relativeTime';
import {
  CATEGORY_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  autorLegible,
  categoryLabel,
  nombreAutor,
  priorityLabel,
  statusLabel,
  usuarioActual,
} from './feedbackConfig';
import api from '../services/api';

const TONO_ESTADO = {
  new: 'list-badge--accent',
  triaged: 'list-badge--warn',
  planned: 'list-badge',
  in_progress: 'list-badge--accent',
  done: 'list-badge--on',
  rejected: 'list-badge--muted',
};

const TONO_PRIORIDAD = {
  critical: 'list-badge--off',
  high: 'list-badge--warn',
  medium: 'list-badge',
  low: 'list-badge--muted',
};

const TONO_CATEGORIA = {
  bug: 'list-badge--off',
  suggestion: 'list-badge--accent',
  roadmap: 'list-badge',
};

const FORM_VACIO = {
  title: '',
  description: '',
  descriptionHtml: '',
  category: 'suggestion',
  priority: 'medium',
  roadmap_target: '',
  reporter_name: '',
  // Los tickets son globales por defecto: los ve cualquier empresa. Solo el
  // superadmin puede crear uno privado (que únicamente ve su empresa).
  is_public: true,
};

const ETIQUETA_GLOBAL = 'Global (todas las empresas)';
const ETIQUETA_PRIVADO = 'Privado (solo esta empresa)';

export default function FeedbackHub({ companyId }) {
  const soySuperadmin = useMemo(() => usuarioActual().superadmin, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [items, setItems] = useState([]);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterVisibility, setFilterVisibility] = useState('');
  const [filterRoadmap, setFilterRoadmap] = useState('');
  const [filterReporter, setFilterReporter] = useState('');
  const [filterOpenedBy, setFilterOpenedBy] = useState('');
  const [groupMode, setGroupMode] = useState('');

  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [pendingAttachments, setPendingAttachments] = useState([]);

  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingText, setEditingText] = useState(false);
  const [textForm, setTextForm] = useState({ title: '', html: '', text: '' });
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getFeedbackItems({ companyId });
      setItems(res?.data || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los tickets');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  const mostrarAviso = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(''), 2500);
  };

  /** ¿Puede editarlo/borrarlo? La empresa dueña o el superadmin de plataforma. */
  const puedeGestionar = useCallback(
    (t) => Boolean(t) && (soySuperadmin || Number(t.company_id) === Number(companyId)),
    [soySuperadmin, companyId],
  );

  /* ── detalle ────────────────────────────────────────────────────────────── */
  const abrirDetalle = (item) => {
    setOpenId(item.id);
    setEditingText(false);
    setNoteText('');
    setConfirmandoBorrado(false);
  };

  const cargarDetalle = useCallback(async () => {
    if (!openId || !companyId) { setDetail(null); setNotes([]); return; }
    setDetailLoading(true);
    try {
      const [detalle, notas] = await Promise.all([
        api.getFeedbackItem(openId, companyId),
        api.getCiNotes('feedback', openId, companyId).catch(() => ({ notes: [] })),
      ]);
      setDetail(detalle?.data || null);
      setNotes(notas?.notes || []);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el ticket');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [openId, companyId]);

  useEffect(() => { cargarDetalle(); }, [cargarDetalle]);

  const ticket = useMemo(
    () => items.find((i) => i.id === openId) || detail || null,
    [items, openId, detail],
  );

  // Un ticket global de OTRA empresa se puede LEER y COMPLETAR (estado,
  // prioridad, categoría, texto…): es un tablón compartido. Lo que sigue
  // reservado a la empresa dueña es BORRARLO (`puedeGestionar`).
  const editable = Boolean(ticket);

  const parchear = async (cambios, mensaje) => {
    if (!openId) return;
    try {
      await api.updateFeedbackItem(openId, cambios, companyId);
      mostrarAviso(mensaje);
      await Promise.all([cargar(), cargarDetalle()]);
    } catch (err) {
      setError(err.message || 'No se pudo guardar el cambio');
    }
  };

  const votar = async (itemId) => {
    try {
      await api.voteFeedbackItem(itemId, companyId);
      mostrarAviso('Voto registrado');
      await Promise.all([cargar(), itemId === openId ? cargarDetalle() : Promise.resolve()]);
    } catch (err) {
      setError(err.message || 'No se pudo registrar el voto');
    }
  };

  const guardarTexto = async () => {
    await parchear(
      {
        title: textForm.title.trim(),
        description: textForm.text.trim(),
        description_html: textForm.html || null,
      },
      'Ticket actualizado',
    );
    setEditingText(false);
  };

  const añadirComentario = async () => {
    if (!noteText.trim() || !openId) return;
    setNoteSaving(true);
    try {
      await api.createCiNote(
        { ci_type: 'feedback', ci_id: openId, text: noteText.trim() },
        companyId,
      );
      setNoteText('');
      await cargarDetalle();
    } catch (err) {
      setError(err.message || 'No se pudo añadir el comentario');
    } finally {
      setNoteSaving(false);
    }
  };

  const borrarAdjunto = async (attachment) => {
    try {
      await api.deleteFeedbackAttachment(attachment.id, companyId);
      await cargarDetalle();
    } catch (err) {
      setError(err.message || 'No se pudo borrar el adjunto');
    }
  };

  const borrarTicket = async (item) => {
    if (!item) return;
    try {
      await api.deleteFeedbackItem(item.id, companyId);
      if (openId === item.id) setOpenId(null);
      mostrarAviso('Ticket eliminado');
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo borrar el ticket');
    }
  };

  /* ── alta ───────────────────────────────────────────────────────────────── */
  const crear = async () => {
    const hasText = Boolean(form.description.trim());
    const hasContent = hasText || /<img/i.test(form.descriptionHtml || '') || pendingAttachments.length > 0;
    if (!form.title.trim()) { setError('El título es obligatorio.'); return; }
    if (!hasContent) { setError('Escribe una descripción o adjunta una imagen o un fichero.'); return; }
    setSaving(true);
    setError('');
    try {
      await api.createFeedbackItem(
        {
          title: form.title.trim(),
          description: form.description.trim(),
          description_html: form.descriptionHtml || null,
          attachment_ids: pendingAttachments.map((a) => a.id),
          category: form.category,
          priority: form.priority,
          roadmap_target: form.roadmap_target.trim() || null,
          reporter_name: form.reporter_name.trim() || null,
          is_public: form.is_public,
        },
        companyId,
      );
      setCreating(false);
      setForm(FORM_VACIO);
      setPendingAttachments([]);
      mostrarAviso('Ticket publicado');
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo crear el ticket');
    } finally {
      setSaving(false);
    }
  };

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  /** ¿Lo abrí yo? El backend guarda el correo (y el id en los tickets antiguos). */
  const esMio = useCallback((t) => {
    const yo = usuarioActual();
    if (!t.created_by) return false;
    // Vale tanto el valor guardado como el nombre que resuelve el backend.
    const candidatos = [String(t.created_by || ''), String(t.created_by_name || '')];
    return candidatos.some((c) => c && (c === yo.email || c === yo.nombre));
  }, []);

  const autores = useMemo(() => {
    // El valor sigue siendo lo que hay en la base (para filtrar), pero lo que se
    // lee es el nombre que resuelve el backend desde el directorio del IdP.
    const vistos = new Map();
    items.forEach((t) => {
      if (t.created_by) vistos.set(t.created_by, nombreAutor(t));
    });
    return [...vistos.entries()]
      .sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'es'))
      .map(([value, label]) => ({ value, label }));
  }, [items]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((t) => {
      if (filterStatus && t.status !== filterStatus) return false;
      if (filterCategory && t.category !== filterCategory) return false;
      if (filterPriority && t.priority !== filterPriority) return false;
      if (filterVisibility === 'public' && !t.is_public) return false;
      if (filterVisibility === 'internal' && t.is_public) return false;
      if (filterRoadmap === 'yes' && !t.roadmap_target) return false;
      if (filterRoadmap === 'no' && t.roadmap_target) return false;
      if (filterReporter && (t.reporter_name || '') !== filterReporter) return false;
      if (filterOpenedBy === 'me' && !esMio(t)) return false;
      if (filterOpenedBy && filterOpenedBy !== 'me' && (t.created_by || '') !== filterOpenedBy) return false;
      if (!term) return true;
      return [t.title, t.description, t.roadmap_target, t.reporter_name, t.created_by, t.created_by_name, categoryLabel(t.category)]
        .filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [items, search, filterStatus, filterCategory, filterPriority, filterVisibility, filterRoadmap, filterReporter, filterOpenedBy, esMio]);

  const limpiarFiltros = () => {
    setSearch(''); setFilterStatus(''); setFilterCategory(''); setFilterPriority('');
    setFilterVisibility(''); setFilterRoadmap(''); setFilterReporter(''); setFilterOpenedBy('');
  };

  const reporters = useMemo(() => {
    const valores = [...new Set(items.map((t) => t.reporter_name).filter(Boolean))];
    return valores.sort().map((v) => ({ value: v, label: v }));
  }, [items]);

  const cuenta = useMemo(() => ({
    total: items.length,
    nuevos: items.filter((t) => t.status === 'new').length,
    enCurso: items.filter((t) => t.status === 'in_progress').length,
    hechos: items.filter((t) => t.status === 'done').length,
    votos: items.reduce((sum, t) => sum + (t.votes || 0), 0),
  }), [items]);

  const kpis = (
    <div className="list-kpis list-kpis--chips">
      <button type="button" className="list-kpi-chip" onClick={limpiarFiltros} title="Ver todos los tickets">
        <span className="list-kpi-value">{cuenta.total}</span>
        <span className="list-kpi-label">tickets</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { limpiarFiltros(); setFilterStatus('new'); }} title="Ver los que nadie ha mirado todavía">
        <span className="list-kpi-value">{cuenta.nuevos}</span>
        <span className="list-kpi-label">nuevos</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { limpiarFiltros(); setFilterStatus('in_progress'); }} title="Ver los que están en curso">
        <span className="list-kpi-value">{cuenta.enCurso}</span>
        <span className="list-kpi-label">en curso</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { limpiarFiltros(); setFilterStatus('done'); }} title="Ver los completados">
        <span className="list-kpi-value">{cuenta.hechos}</span>
        <span className="list-kpi-label">completados</span>
      </button>
      <div className="list-kpi-chip" title="Suma de votos de todos los tickets">
        <span className="list-kpi-value">{cuenta.votos}</span>
        <span className="list-kpi-label">votos</span>
      </div>
    </div>
  );

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'title',
        label: 'Ticket',
        sortable: true,
        render: (t) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontWeight: 600 }}>{t.title}</span>
            <span className="list-hint list-code">#{t.id}</span>
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Estado',
        sortable: true,
        sortValue: (t) => statusLabel(t.status),
        render: (t) => (
          <span className={`list-badge ${TONO_ESTADO[t.status] || 'list-badge--muted'}`}>
            {statusLabel(t.status)}
          </span>
        ),
      },
      {
        key: 'category',
        label: 'Categoría',
        sortable: true,
        sortValue: (t) => categoryLabel(t.category),
        render: (t) => (
          <span className={`list-badge ${TONO_CATEGORIA[t.category] || 'list-badge--muted'}`}>
            {categoryLabel(t.category)}
          </span>
        ),
      },
      {
        key: 'priority',
        label: 'Prioridad',
        sortable: true,
        sortValue: (t) => priorityLabel(t.priority),
        render: (t) => (
          <span className={`list-badge ${TONO_PRIORIDAD[t.priority] || 'list-badge--muted'}`}>
            {priorityLabel(t.priority)}
          </span>
        ),
      },
      {
        key: 'votes',
        label: 'Votos',
        sortable: true,
        render: (t) => <span className="list-code">{t.votes || 0}</span>,
      },
      {
        key: 'roadmap_target',
        label: 'Objetivo',
        sortable: true,
        render: (t) => (t.roadmap_target ? <span className="list-badge">{t.roadmap_target}</span> : dash),
      },
      {
        key: 'visibility',
        label: 'Visibilidad',
        sortable: true,
        sortValue: (t) => (t.is_public ? ETIQUETA_GLOBAL : ETIQUETA_PRIVADO),
        render: (t) => (t.is_public
          ? <span className="list-hint" title="Lo ve cualquier empresa">Global</span>
          : <span className="list-hint" title="Solo lo ve la empresa que lo abrió">Privado</span>),
      },
      {
        key: 'created_by',
        label: 'Abierto por',
        sortable: true,
        sortValue: (t) => nombreAutor(t),
        render: (t) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span>{nombreAutor(t)}</span>
            {t.created_at && <span className="list-hint">{hace(t.created_at)}</span>}
          </span>
        ),
      },
      {
        key: 'reporter_name',
        label: 'Reportado por',
        sortable: true,
        render: (t) => (t.reporter_name ? t.reporter_name : <span className="list-hint">Sin nombre</span>),
      },
      {
        key: 'created_at',
        label: 'Creado',
        sortable: true,
        render: (t) => (t.created_at
          ? (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{new Date(t.created_at).toLocaleDateString()}</span>
              <span className="list-hint">{hace(t.created_at)}</span>
            </span>
          )
          : dash),
      },
      {
        key: 'updated_at',
        label: 'Último cambio',
        sortable: true,
        render: (t) => (t.updated_at ? new Date(t.updated_at).toLocaleString() : dash),
      },
    ];
  }, []);

  const filters = [
    {
      key: 'status', label: 'Estado', allLabel: 'Cualquier estado',
      value: filterStatus, onChange: setFilterStatus, options: STATUS_OPTIONS,
    },
    {
      key: 'category', label: 'Categoría', allLabel: 'Cualquier categoría',
      value: filterCategory, onChange: setFilterCategory, options: CATEGORY_OPTIONS,
    },
    {
      key: 'priority', label: 'Prioridad', allLabel: 'Cualquier prioridad',
      value: filterPriority, onChange: setFilterPriority, options: PRIORITY_OPTIONS,
    },
    {
      key: 'visibility', label: 'Visibilidad', allLabel: 'Todo',
      value: filterVisibility, onChange: setFilterVisibility,
      options: [
        { value: 'public', label: 'Solo globales' },
        { value: 'internal', label: 'Solo privados' },
      ],
    },
    {
      key: 'roadmap', label: 'Roadmap', allLabel: 'Con o sin objetivo',
      value: filterRoadmap, onChange: setFilterRoadmap,
      options: [
        { value: 'yes', label: 'Con objetivo de roadmap' },
        { value: 'no', label: 'Sin objetivo' },
      ],
    },
    {
      key: 'reporter', label: 'Reportado por', allLabel: 'Cualquiera',
      value: filterReporter, onChange: setFilterReporter, options: reporters,
    },
    {
      key: 'openedby', label: 'Abierto por', allLabel: 'Lo abra quien lo abra',
      value: filterOpenedBy, onChange: setFilterOpenedBy,
      options: [
        { value: 'me', label: 'Solo los que abrí yo' },
        ...autores,
      ],
    },
    {
      key: 'group', label: 'Agrupar por', allLabel: 'Sin agrupar',
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'status', label: 'Estado' },
        { value: 'category', label: 'Categoría' },
        { value: 'priority', label: 'Prioridad' },
        { value: 'reporter', label: 'Quién lo reporta' },
        { value: 'openedby', label: 'Quién lo abrió' },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'status') return (t) => statusLabel(t.status);
    if (groupMode === 'category') return (t) => categoryLabel(t.category);
    if (groupMode === 'priority') return (t) => priorityLabel(t.priority);
    if (groupMode === 'reporter') return (t) => t.reporter_name || 'Sin nombre';
    if (groupMode === 'openedby') return (t) => nombreAutor(t);
    return undefined;
  }, [groupMode]);

  const exportRows = useMemo(() => filtered.map((t) => ({
    'Ticket': `#${t.id}`,
    'Título': t.title,
    'Estado': statusLabel(t.status),
    'Categoría': categoryLabel(t.category),
    'Prioridad': priorityLabel(t.priority),
    'Votos': t.votes || 0,
    'Objetivo': t.roadmap_target || '',
    'Visibilidad': t.is_public ? ETIQUETA_GLOBAL : ETIQUETA_PRIVADO,
    'Abierto por': nombreAutor(t),
    'Reportado por': t.reporter_name || '',
    'Descripción': (t.description || '').slice(0, 500),
    'Creado': t.created_at ? new Date(t.created_at).toLocaleString() : '',
  })), [filtered]);

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1rem 0' }}>
        <div className="list-hint">Selecciona una empresa para ver sus tickets.</div>
      </div>
    );
  }

  return (
    <>
      <ListPage
        icon={ChatBubbleLeftRightIcon}
        title="Feedback Hub"
        subtitle={`${items.length} tickets · ${filtered.length} filtrados`}

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por título, descripción u objetivo…"
        filters={filters}

        exportRows={exportRows}
        exportFileBase="feedback"
        exportSheetName="Tickets"

        columns={columns}
        columnStorageKey="cmdb_cols_feedback"
        defaultVisible={['title', 'status', 'category', 'priority', 'votes', 'roadmap_target', 'created_by', 'created_at']}
        rows={filtered}
        rowKey={(t) => t.id}
        rowTitle={(t) => `Ver el ticket #${t.id}`}
        onRowClick={abrirDetalle}
        groupBy={groupBy}

        kpis={kpis}

        onCreate={() => { setForm(FORM_VACIO); setPendingAttachments([]); setCreating(true); }}
        createLabel="Nuevo ticket"
        createTitle="Reporta un bug, una sugerencia o algo para el roadmap"

        headerActions={(
          <>
            {aviso && <span className="list-badge list-badge--on"><CheckIcon style={{ width: 13, height: 13, verticalAlign: '-2px' }} /> {aviso}</span>}
            <button type="button" className="list-btn-secondary" onClick={cargar} disabled={loading}>
              <ArrowPathIcon style={{ width: 15, height: 15 }} /> {loading ? 'Cargando…' : 'Actualizar'}
            </button>
          </>
        )}

        views={[
          { key: 'list', label: 'Lista', icon: ListBulletIcon, title: 'Tabla con todas las columnas' },
          {
            key: 'card', label: 'Fichas', icon: Squares2X2Icon,
            title: 'Tarjetas con lo esencial de cada ticket',
            render: ({ rows, rowActions }) => (
              <div className="list-cards">
                {rows.map((t) => (
                  <div key={t.id} className="list-card">
                    <div className="list-card-head">
                      <div>
                        <div className="list-card-title">{t.title}</div>
                        <span className="list-hint list-code">#{t.id} · {hace(t.created_at)}</span>
                      </div>
                      <span className={`list-badge ${TONO_ESTADO[t.status] || 'list-badge--muted'}`}>
                        {statusLabel(t.status)}
                      </span>
                    </div>
                    <div className="list-card-meta">
                      <span className={`list-badge ${TONO_CATEGORIA[t.category] || 'list-badge--muted'}`}>
                        {categoryLabel(t.category)}
                      </span>
                      <span className={`list-badge ${TONO_PRIORIDAD[t.priority] || 'list-badge--muted'}`}>
                        {priorityLabel(t.priority)}
                      </span>
                      {t.roadmap_target && <span className="list-badge">{t.roadmap_target}</span>}
                      {!t.is_public && <span className="list-hint">Privado</span>}
                      <span className="list-hint">Abierto por {nombreAutor(t)}</span>
                    </div>
                    <div className="list-card-actions">
                      <button
                        type="button"
                        className="list-action-btn"
                        onClick={() => votar(t.id)}
                        title={`Votar (${t.votes || 0})`}
                      >
                        <HandThumbUpIcon />
                      </button>
                      <button
                        type="button"
                        className="list-action-btn"
                        onClick={() => abrirDetalle(t)}
                        title="Ver el ticket"
                      >
                        <DocumentTextIcon />
                      </button>
                      {rowActions(t)}
                    </div>
                  </div>
                ))}
              </div>
            ),
          },
        ]}
        listKey="feedback"
        defaultView="list"

        actions={{
          extra: (t) => (
            <>
              <button
                type="button"
                className="list-action-btn"
                onClick={() => votar(t.id)}
                title={`Votar (${t.votes || 0} votos)`}
              >
                <HandThumbUpIcon />
              </button>
              <button
                type="button"
                className="list-action-btn"
                onClick={() => abrirDetalle(t)}
                title="Ver el ticket: descripción, adjuntos e historial"
              >
                <DocumentTextIcon />
              </button>
            </>
          ),
          onDelete: borrarTicket,
          canDelete: (t) => puedeGestionar(t),
          deleteTitle: 'Eliminar ticket',
          deleteMessage: (t) => `¿Seguro que quieres eliminar el ticket #${t.id} «${t.title}»? Se borran también sus adjuntos e historial.`,
        }}

        loading={loading && items.length === 0}
        loadingText="Cargando los tickets…"
        error={error}

        empty={{
          icon: '💬',
          title: items.length === 0 ? 'Todavía no hay tickets' : 'Ningún ticket coincide con los filtros',
          subtitle: items.length === 0
            ? 'Aquí van los bugs, las sugerencias y lo que se quiere hacer (roadmap). Los tickets globales los ve cualquier empresa; los privados, solo la que los abre.'
            : undefined,
          action: items.length === 0 ? (
            <button type="button" className="list-btn-primary" onClick={() => setCreating(true)}>
              <PlusIcon style={{ width: 14, height: 14 }} /> Nuevo ticket
            </button>
          ) : undefined,
        }}
      />

      {/* ══ Detalle del ticket ══ */}
      {openId && (
        <Dialog
          title={ticket ? `#${ticket.id} · ${ticket.title}` : 'Ticket'}
          icon={<ChatBubbleLeftRightIcon style={{ width: 18, height: 18 }} />}
          maxWidth={780}
          busy={detailLoading}
          onClose={() => { setOpenId(null); setConfirmandoBorrado(false); }}
          footer={(
            <>
              {ticket && puedeGestionar(ticket) && (
                confirmandoBorrado ? (
                  <button
                    type="button"
                    className="list-btn-danger"
                    style={{ marginRight: 'auto' }}
                    onClick={() => borrarTicket(ticket)}
                  >
                    <TrashIcon style={{ width: 15, height: 15 }} /> Sí, eliminar el ticket
                  </button>
                ) : (
                  <button
                    type="button"
                    className="list-btn-secondary"
                    style={{ marginRight: 'auto' }}
                    onClick={() => setConfirmandoBorrado(true)}
                  >
                    <TrashIcon style={{ width: 15, height: 15 }} /> Eliminar
                  </button>
                )
              )}
              <button type="button" className="list-btn-secondary" onClick={() => { setOpenId(null); setConfirmandoBorrado(false); }}>
                {confirmandoBorrado ? 'Cancelar' : 'Cerrar'}
              </button>
            </>
          )}
        >
          {detailLoading && !ticket ? (
            <div className="list-loading">
              <div className="spinner-border text-primary" aria-hidden="true" />
              <span>Cargando el ticket…</span>
            </div>
          ) : ticket && (
            <>
              {/* Estado visible de un vistazo + metadatos del ticket */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
                <span className={`list-badge ${TONO_ESTADO[ticket.status] || 'list-badge'}`} style={{ fontSize: '0.8rem', padding: '5px 12px' }}>
                  {statusLabel(ticket.status)}
                </span>
                <span className={`list-badge ${TONO_PRIORIDAD[ticket.priority] || 'list-badge'}`}>
                  {priorityLabel(ticket.priority)}
                </span>
                <span className={`list-badge ${TONO_CATEGORIA[ticket.category] || 'list-badge'}`}>
                  {categoryLabel(ticket.category)}
                </span>
                {ticket.roadmap_target && (
                  <span className="list-badge list-badge--muted">Roadmap {ticket.roadmap_target}</span>
                )}
                <span className="list-badge list-badge--muted">
                  <HandThumbUpIcon style={{ width: 12, height: 12 }} /> {ticket.votes || 0}
                </span>
              </div>

              {/* Estado y prioridad se guardan al cambiarlos */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
                <label className="list-hint" style={{ flex: '1 1 160px' }}>
                  Estado
                  <select
                    className="list-select"
                    style={{ width: '100%', marginTop: 4 }}
                    value={ticket.status}
                    disabled={!editable}
                    onChange={(e) => parchear({ status: e.target.value }, 'Estado actualizado')}
                  >
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <label className="list-hint" style={{ flex: '1 1 160px' }}>
                  Prioridad
                  <select
                    className="list-select"
                    style={{ width: '100%', marginTop: 4 }}
                    value={ticket.priority}
                    disabled={!editable}
                    onChange={(e) => parchear({ priority: e.target.value }, 'Prioridad actualizada')}
                  >
                    {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <label className="list-hint" style={{ flex: '1 1 160px' }}>
                  Categoría
                  <select
                    className="list-select"
                    style={{ width: '100%', marginTop: 4 }}
                    value={ticket.category}
                    disabled={!editable}
                    onChange={(e) => parchear({ category: e.target.value }, 'Categoría actualizada')}
                  >
                    {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <button
                  type="button"
                  className="list-btn-secondary"
                  onClick={() => votar(ticket.id)}
                  title="Sumar un voto a este ticket"
                >
                  <HandThumbUpIcon style={{ width: 15, height: 15 }} /> Votar ({ticket.votes || 0})
                </button>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
                <label className="list-hint" style={{ flex: '1 1 200px' }}>
                  Objetivo de roadmap (opcional)
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    defaultValue={ticket.roadmap_target || ''}
                    placeholder="ej. Q2 2026"
                    disabled={!editable}
                    onBlur={(e) => {
                      const valor = e.target.value.trim();
                      if (valor !== (ticket.roadmap_target || '')) {
                        parchear({ roadmap_target: valor || null }, 'Objetivo actualizado');
                      }
                    }}
                  />
                </label>
                <label className="list-hint" style={{ flex: '1 1 220px' }}>
                  Reportado por
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    defaultValue={ticket.reporter_name || ''}
                    placeholder="Nombre de quien lo reporta"
                    disabled={!editable}
                    onBlur={(e) => {
                      const valor = e.target.value.trim();
                      if (valor !== (ticket.reporter_name || '')) {
                        parchear({ reporter_name: valor || null }, 'Autor actualizado');
                      }
                    }}
                  />
                </label>
                {/* La opción privada solo existe para el superadmin: las empresas
                    cliente nunca crean tickets privados, así que no se les enseña. */}
                {soySuperadmin && (
                  <label
                    className="list-hint"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 1 280px' }}
                    title="Global: lo ve cualquier empresa. Privado: solo la empresa que lo abrió"
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(ticket.is_public)}
                      onChange={(e) => parchear({ is_public: e.target.checked }, e.target.checked ? 'Ticket global' : 'Ticket privado')}
                    />
                    {ticket.is_public
                      ? <><GlobeAltIcon style={{ width: 15, height: 15 }} /> {ETIQUETA_GLOBAL}</>
                      : <><LockClosedIcon style={{ width: 15, height: 15 }} /> {ETIQUETA_PRIVADO}</>}
                  </label>
                )}
              </div>

              <div className="list-hint" style={{ marginBottom: 8, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span>
                  Abierto por <strong>{nombreAutor(ticket)}</strong>
                  {ticket.created_at ? ` · ${new Date(ticket.created_at).toLocaleString()}` : ''}
                </span>
                {ticket.status_changed_at && (
                  <span>
                    Estado cambiado {hace(ticket.status_changed_at)}
                    {ticket.status_changed_by ? ` por ${autorLegible(ticket.status_changed_by)}` : ''}
                  </span>
                )}
                {ticket.updated_by && ticket.updated_by !== ticket.created_by && (
                  <span>Último cambio por {nombreAutor(ticket, 'updated_by')}</span>
                )}
              </div>

              {/* Descripción con formato, con edición en el sitio */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontWeight: 'var(--fw-semibold)', fontSize: '0.82rem' }}>Descripción</span>
                {editable && (
                <button
                  type="button"
                  className="list-btn-secondary"
                  style={{ padding: '3px 10px', fontSize: '0.74rem', marginLeft: 'auto' }}
                  onClick={() => {
                    setTextForm({
                      title: ticket.title || '',
                      html: detail?.description_html || '',
                      text: ticket.description || '',
                    });
                    setEditingText((v) => !v);
                  }}
                >
                  <PencilSquareIcon style={{ width: 13, height: 13 }} /> {editingText ? 'Cancelar' : 'Editar el texto'}
                </button>
                )}
              </div>

              {editingText ? (
                <div>
                  <label className="list-hint" style={{ display: 'block', marginBottom: 8 }}>
                    Título
                    <input
                      className="list-input"
                      style={{ width: '100%', marginTop: 4 }}
                      value={textForm.title}
                      onChange={(e) => setTextForm((p) => ({ ...p, title: e.target.value }))}
                    />
                  </label>
                  <RichTextEditor
                    companyId={companyId}
                    value={textForm.html}
                    onChange={(html, text) => setTextForm((p) => ({ ...p, html, text }))}
                    onError={setError}
                    placeholder="Describe el problema, la sugerencia o la mejora."
                    minHeight={160}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button type="button" className="list-btn-primary" onClick={guardarTexto}>
                      Guardar el texto
                    </button>
                  </div>
                </div>
              ) : (detail?.description_html ? (
                <div
                  style={{
                    padding: '10px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--bg-surface)',
                    color: 'var(--text-primary)',
                    fontSize: '0.86rem',
                  }}
                  // El HTML llega saneado del servidor (bleach).
                  dangerouslySetInnerHTML={{ __html: detail.description_html }}
                />
              ) : (
                <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>
                  {ticket.description || 'Sin descripción.'}
                </p>
              ))}

              {/* Adjuntos */}
              <div style={{ marginTop: 14 }}>
                <FeedbackAttachmentsField
                  companyId={companyId}
                  feedbackId={ticket.id}
                  attachments={detail?.attachments || []}
                  onUploaded={cargarDetalle}
                  onRemoved={editable ? borrarAdjunto : undefined}
                  onError={setError}
                  disabled={!editable}
                  hint="Capturas, logs, PDFs…"
                />
              </div>

              {/* Historial de comentarios */}
              <div
                style={{
                  marginTop: 22,
                  marginBottom: 26,
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md, 10px)',
                  background: 'var(--bg-surface)',
                  padding: '12px 14px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <ChatBubbleOvalLeftEllipsisIcon style={{ width: 16, height: 16, color: 'var(--text-secondary)' }} />
                  <span style={{ fontWeight: 'var(--fw-semibold)', fontSize: '0.82rem' }}>
                    Historial visible ({notes.length})
                  </span>
                </div>
                {notes.length === 0 ? (
                  <p className="list-hint" style={{ margin: 0 }}>Sin comentarios todavía.</p>
                ) : notes.map((n) => (
                  <div key={n.id} style={{ padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                    <div className="list-hint">
                      <strong>{n.created_by || 'Usuario'}</strong>
                      {n.created_at ? ` · ${new Date(n.created_at).toLocaleString()}` : ''}
                    </div>
                    <div style={{ fontSize: '0.86rem', whiteSpace: 'pre-wrap' }}>{n.text}</div>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 10 }}>
                  <label className="list-hint" style={{ flex: 1 }}>
                    Añadir comentario
                    <input
                      className="list-input"
                      style={{ width: '100%', marginTop: 4 }}
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') añadirComentario(); }}
                      placeholder="Una nota para el equipo…"
                    />
                  </label>
                  <button
                    type="button"
                    className="list-btn-primary"
                    onClick={añadirComentario}
                    disabled={noteSaving || !noteText.trim()}
                  >
                    {noteSaving ? 'Guardando…' : 'Comentar'}
                  </button>
                </div>
              </div>

              {detail?.attachments?.length ? (
                <p className="list-hint" style={{ marginTop: 10, marginBottom: 0 }}>
                  <PaperClipIcon style={{ width: 13, height: 13, verticalAlign: '-2px' }} />{' '}
                  {detail.attachments.length} adjunto{detail.attachments.length === 1 ? '' : 's'} en este ticket.
                </p>
              ) : null}
            </>
          )}
        </Dialog>
      )}

      {/* ══ Alta de ticket ══ */}
      {creating && (
        <Dialog
          title="Nuevo ticket"
          icon={<PlusIcon style={{ width: 18, height: 18 }} />}
          maxWidth={720}
          busy={saving}
          onClose={() => setCreating(false)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setCreating(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="list-btn-primary" onClick={crear} disabled={saving || !form.title.trim()}>
                {saving ? 'Publicando…' : 'Publicar ticket'}
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            El ticket queda <strong>a tu nombre</strong>
            {usuarioActual().nombre || usuarioActual().email
              ? <> (<strong>{usuarioActual().nombre || usuarioActual().email}</strong>)</>
              : null}
            : lo registra el servidor a partir de tu sesión, así que en el listado se ve quién lo abrió.
          </p>

          <label className="list-hint" style={{ display: 'block', marginBottom: 10 }}>
            Título
            <input
              className="list-input"
              style={{ width: '100%', marginTop: 4 }}
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Resume el problema o la idea en una línea"
            />
          </label>

          <RichTextEditor
            companyId={companyId}
            value={form.descriptionHtml}
            onChange={(html, text) => setForm((p) => ({ ...p, descriptionHtml: html, description: text }))}
            onError={setError}
            placeholder="Describe el problema, la sugerencia o la mejora. Acepta formato y puedes pegar capturas."
            minHeight={160}
          />

          <div style={{ marginTop: 10 }}>
            <FeedbackAttachmentsField
              companyId={companyId}
              attachments={pendingAttachments}
              onUploaded={(a) => setPendingAttachments((prev) => [...prev, a])}
              onRemoved={async (a) => {
                setPendingAttachments((prev) => prev.filter((x) => x.id !== a.id));
                try { await api.deleteFeedbackAttachment(a.id, companyId); } catch { /* queda huérfano */ }
              }}
              onError={setError}
              hint="Capturas, logs, PDFs…"
            />
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
            <label className="list-hint" style={{ flex: '1 1 160px' }}>
              Categoría
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
              >
                {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="list-hint" style={{ flex: '1 1 160px' }}>
              Prioridad
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
              >
                {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            {form.category === 'roadmap' && (
              <label className="list-hint" style={{ flex: '1 1 200px' }}>
                Objetivo de roadmap
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={form.roadmap_target}
                  onChange={(e) => setForm((p) => ({ ...p, roadmap_target: e.target.value }))}
                  placeholder="ej. Q2 2026"
                />
              </label>
            )}
            <label className="list-hint" style={{ flex: '1 1 200px' }}>
              Reportado por (opcional)
              <input
                className="list-input"
                style={{ width: '100%', marginTop: 4 }}
                value={form.reporter_name}
                onChange={(e) => setForm((p) => ({ ...p, reporter_name: e.target.value }))}
                placeholder="Quién lo pide, si no eres tú"
              />
            </label>
          </div>

          {/* Igual que en la edición: los tickets nacen globales y solo el
              superadmin puede hacerlos privados. Las empresas cliente no ven
              esta opción porque nunca crean tickets privados. */}
          {soySuperadmin && (
            <label
              className="list-hint"
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}
              title="Global: lo ve cualquier empresa. Privado: solo la empresa actual"
            >
              <input
                type="checkbox"
                checked={form.is_public}
                onChange={(e) => setForm((p) => ({ ...p, is_public: e.target.checked }))}
              />
              {form.is_public
                ? <><GlobeAltIcon style={{ width: 15, height: 15 }} /> {ETIQUETA_GLOBAL}</>
                : <><LockClosedIcon style={{ width: 15, height: 15 }} /> {ETIQUETA_PRIVADO}</>}
            </label>
          )}
        </Dialog>
      )}
    </>
  );
}
